import type {
  ReactNode,
  CSSProperties,
  KeyboardEvent,
  HTMLAttributes,
} from "react";

interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  children: ReactNode;
  className?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** padding in px (number) or 0 to disable wrapper padding (header still padded) */
  padding?: number | string;
  elevated?: boolean;
  hover?: boolean;
  onClick?: () => void;
  style?: CSSProperties;
}

export function Card({
  children,
  className = "",
  title,
  subtitle,
  actions,
  padding,
  elevated = false,
  hover = false,
  onClick,
  style,
  ...rest
}: CardProps) {
  const padStyle: CSSProperties =
    padding === 0
      ? { padding: 0 }
      : typeof padding === "number"
        ? { padding }
        : padding
          ? { padding }
          : {};
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };
  return (
    <div
      {...rest}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      style={{ ...style }}
      className={`border border-border rounded-lg overflow-hidden ${
        elevated ? "bg-elevated shadow-sm" : "bg-surface"
      } ${hover ? "transition hover:border-border-strong hover:shadow" : ""} ${
        onClick ? "cursor-pointer" : ""
      } ${className}`}
    >
      {(title || actions) && (
        <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            {title && (
              <div className="font-serif text-base font-medium text-text">
                {title}
              </div>
            )}
            {subtitle && (
              <div className="text-xs text-muted mt-0.5">{subtitle}</div>
            )}
          </div>
          {actions && (
            <div className="flex items-center gap-2 shrink-0">{actions}</div>
          )}
        </div>
      )}
      <div
        style={padStyle}
        className={padding === 0 ? "" : padding ? "" : "p-4"}
      >
        {children}
      </div>
    </div>
  );
}
